import {
  getFromLocalStorage,
  setInLocalStorage,
} from '@ui/utils/hooks/local-storage';
import {Capacitor} from '@capacitor/core';
import {SecureStoragePlugin} from 'capacitor-secure-storage-plugin';

const CACHE_VERSION = 2;
const ACTION_WINDOW_SECONDS = 3600;
const LISTEN_WINDOW_SECONDS = 86400;
const PERSIST_THROTTLE_MS = 5000;
const isNativePlatform = Capacitor.isNativePlatform();

export type RollingQuotaAction = 'next' | 'prev';

interface RollingQuotaBucket {
  limit: number | null;
  windowSeconds: number;
  timestamps: number[];
}

interface ListenQuotaBucket {
  limit: number | null;
  windowSeconds: number;
  timestamps: number[];
}

interface LocalQuotaCacheState {
  version: number;
  userKey: string;
  updatedAt: number;
  actions: Record<RollingQuotaAction, RollingQuotaBucket>;
  listen24h: ListenQuotaBucket;
}

interface BucketDefaults {
  limit: number | null;
  windowSeconds?: number;
}

export interface LocalQuotaDefaults {
  next: BucketDefaults;
  prev: BucketDefaults;
  listen24h: BucketDefaults;
}

export interface LocalQuotaDecision {
  allowed: boolean;
  reason: 'ok' | 'overQuota';
  retryAfterSeconds: number | null;
}

interface CreateLocalPlayerQuotaCacheParams {
  storageKey: string;
  userKey: string;
  defaults: LocalQuotaDefaults;
}

export interface LocalPlayerQuotaCache {
  hydrateFromStorage: () => Promise<void>;
  flushToStorage: () => Promise<void>;
  checkAndConsume: (
    action: RollingQuotaAction,
    consume?: boolean,
  ) => LocalQuotaDecision;
  checkListenLimit: () => LocalQuotaDecision;
  consumeListenSeconds: (seconds?: number) => LocalQuotaDecision;
}

export function createLocalPlayerQuotaCache({
  storageKey,
  userKey,
  defaults,
}: CreateLocalPlayerQuotaCacheParams): LocalPlayerQuotaCache {
  let cache = isNativePlatform
    ? createDefaultCache(userKey, defaults)
    : loadCacheFromStored(
        getFromLocalStorage<LocalQuotaCacheState | null>(storageKey, null),
        userKey,
        defaults,
      );

  let persistQueue: Promise<void> = Promise.resolve();
  let hydratePromise: Promise<void> | null = null;
  let lastPersistAt = 0;
  let pendingPersist = false;

  const queuePersist = (force = false): void => {
    const now = Date.now();
    cache.updatedAt = now;

    if (!force && now - lastPersistAt < PERSIST_THROTTLE_MS) {
      pendingPersist = true;
      return;
    }

    const snapshot = cloneCache(cache);
    if (!snapshot) {
      return;
    }

    pendingPersist = false;
    lastPersistAt = now;
    persistQueue = persistQueue
      .catch(() => {
        //
      })
      .then(() => persistCache(storageKey, snapshot));
  };

  const ensureSameUser = () => {
    if (cache.userKey !== userKey) {
      cache = createDefaultCache(userKey, defaults);
      queuePersist(true);
    }
  };

  const hydrateFromStorage = async () => {
    if (!isNativePlatform) {
      return;
    }

    if (hydratePromise) {
      return hydratePromise;
    }

    hydratePromise = readCacheFromStorage(storageKey)
      .then(stored => {
        if (!stored) return;
        cache = loadCacheFromStored(stored, userKey, defaults);
      })
      .finally(() => {
        hydratePromise = null;
      });

    return hydratePromise;
  };

  const flushToStorage = async () => {
    if (pendingPersist) {
      queuePersist(true);
    }
    return persistQueue;
  };

  const checkAndConsume = (
    action: RollingQuotaAction,
    consume = true,
  ): LocalQuotaDecision => {
    ensureSameUser();

    const bucket = cache.actions[action];
    const now = Date.now();
    pruneTimestampBucket(bucket, now);

    const decision = checkTimestampBucket(bucket, now);
    if (!decision.allowed || !consume) {
      return decision;
    }

    bucket.timestamps.push(now);
    queuePersist(true);

    return {
      allowed: true,
      reason: 'ok',
      retryAfterSeconds: null,
    };
  };

  const checkListenLimit = (): LocalQuotaDecision => {
    ensureSameUser();

    const bucket = cache.listen24h;
    const now = Date.now();
    pruneTimestampBucket(bucket, now);

    return checkTimestampBucket(bucket, now);
  };

  const consumeListenSeconds = (seconds = 1): LocalQuotaDecision => {
    ensureSameUser();

    const bucket = cache.listen24h;
    const now = Date.now();
    pruneTimestampBucket(bucket, now);

    const decision = checkTimestampBucket(bucket, now);
    if (!decision.allowed) {
      return decision;
    }

    const safeSeconds = Math.max(0, Math.floor(seconds));
    for (let i = 0; i < safeSeconds; i++) {
      bucket.timestamps.push(now + i);
    }

    queuePersist(false);

    return {
      allowed: true,
      reason: 'ok',
      retryAfterSeconds: null,
    };
  };

  return {
    hydrateFromStorage,
    flushToStorage,
    checkAndConsume,
    checkListenLimit,
    consumeListenSeconds,
  };
}

function checkTimestampBucket(
  bucket: {limit: number | null; windowSeconds: number; timestamps: number[]},
  nowMs: number,
): LocalQuotaDecision {
  if (bucket.limit == null) {
    return {
      allowed: true,
      reason: 'ok',
      retryAfterSeconds: null,
    };
  }

  if (bucket.timestamps.length < bucket.limit) {
    return {
      allowed: true,
      reason: 'ok',
      retryAfterSeconds: null,
    };
  }

  const oldestTs = bucket.timestamps[0];
  const retryAfterSeconds = oldestTs
    ? Math.max(
        0,
        Math.ceil((oldestTs + bucket.windowSeconds * 1000 - nowMs) / 1000),
      )
    : 0;

  return {
    allowed: false,
    reason: 'overQuota',
    retryAfterSeconds,
  };
}

function loadCacheFromStored(
  stored: LocalQuotaCacheState | null,
  userKey: string,
  defaults: LocalQuotaDefaults,
): LocalQuotaCacheState {
  if (!stored || stored.version !== CACHE_VERSION || stored.userKey !== userKey) {
    return createDefaultCache(userKey, defaults);
  }

  const now = Date.now();
  const next = sanitizeRollingBucket(stored.actions?.next, defaults.next, now);
  const prev = sanitizeRollingBucket(stored.actions?.prev, defaults.prev, now);
  const listen24h = sanitizeListenBucket(stored.listen24h, defaults.listen24h, now);

  return {
    version: CACHE_VERSION,
    userKey,
    updatedAt: now,
    actions: {next, prev},
    listen24h,
  };
}

async function readCacheFromStorage(
  storageKey: string,
): Promise<LocalQuotaCacheState | null> {
  if (!isNativePlatform) {
    return getFromLocalStorage<LocalQuotaCacheState | null>(storageKey, null);
  }

  try {
    const result = await SecureStoragePlugin.get({key: storageKey});
    if (!result?.value) {
      return null;
    }

    return JSON.parse(result.value) as LocalQuotaCacheState;
  } catch {
    return null;
  }
}

async function persistCache(storageKey: string, cache: LocalQuotaCacheState) {
  if (!isNativePlatform) {
    setInLocalStorage(storageKey, cache);
    return;
  }

  try {
    await SecureStoragePlugin.set({
      key: storageKey,
      value: JSON.stringify(cache),
    });
  } catch {
    //
  }
}

function cloneCache(
  cache: LocalQuotaCacheState,
): LocalQuotaCacheState | null {
  try {
    return JSON.parse(JSON.stringify(cache)) as LocalQuotaCacheState;
  } catch {
    return null;
  }
}

function createDefaultCache(
  userKey: string,
  defaults: LocalQuotaDefaults,
): LocalQuotaCacheState {
  const now = Date.now();
  return {
    version: CACHE_VERSION,
    userKey,
    updatedAt: now,
    actions: {
      next: createRollingBucket(defaults.next),
      prev: createRollingBucket(defaults.prev),
    },
    listen24h: createListenBucket(defaults.listen24h),
  };
}

function createRollingBucket(defaults: BucketDefaults): RollingQuotaBucket {
  return {
    limit: normalizeLimit(defaults.limit),
    windowSeconds: normalizeWindow(defaults.windowSeconds, ACTION_WINDOW_SECONDS),
    timestamps: [],
  };
}

function createListenBucket(defaults: BucketDefaults): ListenQuotaBucket {
  return {
    limit: normalizeLimit(defaults.limit),
    windowSeconds: normalizeWindow(defaults.windowSeconds, LISTEN_WINDOW_SECONDS),
    timestamps: [],
  };
}

function sanitizeRollingBucket(
  bucket: RollingQuotaBucket | undefined,
  defaults: BucketDefaults,
  nowMs: number,
): RollingQuotaBucket {
  const sanitized: RollingQuotaBucket = {
    limit: normalizeLimit(defaults.limit),
    windowSeconds: normalizeWindow(
      bucket?.windowSeconds ?? defaults.windowSeconds,
      ACTION_WINDOW_SECONDS,
    ),
    timestamps: sanitizeTimestamps(bucket?.timestamps),
  };

  pruneTimestampBucket(sanitized, nowMs);
  return sanitized;
}

function sanitizeListenBucket(
  bucket: ListenQuotaBucket | undefined,
  defaults: BucketDefaults,
  nowMs: number,
): ListenQuotaBucket {
  const sanitized: ListenQuotaBucket = {
    limit: normalizeLimit(defaults.limit),
    windowSeconds: normalizeWindow(
      bucket?.windowSeconds ?? defaults.windowSeconds,
      LISTEN_WINDOW_SECONDS,
    ),
    timestamps: sanitizeTimestamps(bucket?.timestamps),
  };

  pruneTimestampBucket(sanitized, nowMs);
  return sanitized;
}

function pruneTimestampBucket(
  bucket: {windowSeconds: number; timestamps: number[]},
  nowMs: number,
) {
  if (!bucket.timestamps.length) {
    return;
  }

  const minTs = nowMs - bucket.windowSeconds * 1000;
  let removeCount = 0;

  while (
    removeCount < bucket.timestamps.length &&
    bucket.timestamps[removeCount] < minTs
  ) {
    removeCount++;
  }

  if (removeCount > 0) {
    bucket.timestamps.splice(0, removeCount);
  }
}

function normalizeLimit(value: unknown): number | null {
  if (value === null || value === undefined || value === '' || value === 'null') {
    return null;
  }
  if (typeof value === 'boolean') {
    return null;
  }
  if (!Number.isFinite(Number(value))) {
    return null;
  }
  const normalized = Math.floor(Number(value));
  return normalized >= 0 ? normalized : null;
}

function normalizeWindow(value: unknown, fallback: number): number {
  if (typeof value === 'boolean') {
    return fallback > 0 ? fallback : ACTION_WINDOW_SECONDS;
  }
  if (!Number.isFinite(Number(value))) {
    return fallback > 0 ? fallback : ACTION_WINDOW_SECONDS;
  }
  const normalized = Math.floor(Number(value));
  return normalized > 0 ? normalized : fallback;
}

function sanitizeTimestamps(value: unknown): number[] {
  if (!Array.isArray(value)) return [];

  return value
    .map(item => Number(item))
    .filter(item => Number.isFinite(item) && item > 0)
    .map(item => Math.floor(item))
    .sort((a, b) => a - b);
}
